// Dashboard charts (recharts). Kept presentational: callers own the queries and
// map API shapes to the small Slice/TrendPoint types below. Colours for the
// chart chrome (grid/axes/tooltips) follow the active light/dark theme.
import {
  Bar as RBar,
  BarChart as RBarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "../lib/useTheme";

export interface Slice {
  name: string;
  value: number;
  color: string;
}

export interface TrendPoint {
  id: number;
  label: string; // short x-axis tick
  fullLabel: string; // full timestamp for the tooltip
  documents: number;
  failures: number;
}

// One bar in a HorizontalBarChart. `name` is the category label (truncated on the
// axis); `fullLabel` is the untruncated text shown in the tooltip.
export interface BarDatum {
  name: string;
  value: number;
  color: string;
  fullLabel?: string;
}

// Royal blue from the redesign palette.
const ACCENT = "#2767D5";

type SlicePayload = { name?: string; value?: number; payload?: Slice };

function SliceTooltip({ active, payload }: { active?: boolean; payload?: SlicePayload[] }) {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-md dark:border-[#1E2E4D] dark:bg-panel">
      <span className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full" style={{ background: entry.payload?.color }} />
        <span className="font-medium text-gray-800 dark:text-slate-100">{entry.name}</span>
        <span className="ml-2 tabular-nums text-gray-500 dark:text-slate-400">{(entry.value ?? 0).toLocaleString()}</span>
      </span>
    </div>
  );
}

// Donut with the running total in the middle and a legend underneath.
export function DonutChart({ data, centerValue, centerLabel }: { data: Slice[]; centerValue: string; centerLabel: string }) {
  return (
    <div>
      <div className="relative h-52">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={58} outerRadius={84} paddingAngle={2} stroke="none" isAnimationActive={false}>
              {data.map((s) => (
                <Cell key={s.name} fill={s.color} />
              ))}
            </Pie>
            <Tooltip content={<SliceTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold tabular-nums text-gray-900 dark:text-white">{centerValue}</span>
          <span className="text-xs text-gray-500 dark:text-slate-400">{centerLabel}</span>
        </div>
      </div>
      <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        {data.map((s) => (
          <li key={s.name} className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: s.color }} />
            <span className="text-gray-600 dark:text-slate-400">{s.name}</span>
            <span className="ml-auto font-medium tabular-nums text-gray-900 dark:text-slate-100">{s.value.toLocaleString()}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

type TrendPayload = { payload?: TrendPoint };

function TrendTooltip({ active, payload }: { active?: boolean; payload?: TrendPayload[] }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  if (!p) return null;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-md dark:border-[#1E2E4D] dark:bg-panel">
      <div className="font-medium text-gray-800 dark:text-slate-100">Extraction #{p.id}</div>
      <div className="text-gray-500 dark:text-slate-400">{p.fullLabel}</div>
      <div className="mt-1 flex items-center gap-2 text-gray-700 dark:text-slate-300">
        <span className="h-2 w-2 rounded-full" style={{ background: ACCENT }} />
        {p.documents.toLocaleString()} documents written
      </div>
      <div className="flex items-center gap-2 text-gray-700 dark:text-slate-300">
        <span className="h-2 w-2 rounded-full" style={{ background: "#dc2626" }} />
        {p.failures.toLocaleString()} failure{p.failures === 1 ? "" : "s"}
      </div>
    </div>
  );
}

// Throughput over recent runs. Failures are only drawn when a run had any, so a
// clean pipeline shows a single line instead of a misleading flat series at 0.
export function TrendChart({ data }: { data: TrendPoint[] }) {
  const { theme } = useTheme();
  const dark = theme === "dark";
  const grid = dark ? "#22314E" : "#f1f5f9";
  const tick = dark ? "#7E90AE" : "#9ca3af";
  const axisLine = dark ? "#2A3A5A" : "#e5e7eb";
  const hasFailures = data.some((d) => d.failures > 0);

  return (
    <div className="h-60 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: tick }} tickLine={false} axisLine={{ stroke: axisLine }} minTickGap={24} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: tick }} tickLine={false} axisLine={false} width={44} />
          <Tooltip content={<TrendTooltip />} />
          <Line type="monotone" dataKey="documents" stroke={ACCENT} strokeWidth={2} dot={{ r: 3, fill: ACCENT }} activeDot={{ r: 5 }} isAnimationActive={false} />
          {hasFailures && (
            <Line
              type="monotone"
              dataKey="failures"
              stroke="#dc2626"
              strokeWidth={2}
              strokeDasharray="4 3"
              dot={{ r: 3, fill: "#dc2626" }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Long category labels (e.g. free-text questions) don't fit on the axis, so they
// are clipped here and shown in full in the tooltip.
function truncate(text: string, max: number) {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

type BarPayload = { payload?: BarDatum };

function BarTooltip({ active, payload }: { active?: boolean; payload?: BarPayload[] }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  if (!d) return null;
  return (
    <div className="max-w-xs rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-md dark:border-[#1E2E4D] dark:bg-panel">
      <div className="font-medium text-gray-800 dark:text-slate-100">{d.fullLabel ?? d.name}</div>
      <div className="mt-1 flex items-center gap-2 text-gray-700 dark:text-slate-300">
        <span className="h-2 w-2 rounded-full" style={{ background: d.color }} />
        <span className="tabular-nums">{d.value.toLocaleString()}</span>
      </div>
    </div>
  );
}

// Horizontal bars, largest category first. Callers pass the data already sorted;
// bar height grows with the row count so labels are never cramped.
export function HorizontalBarChart({ data, labelWidth = 190 }: { data: BarDatum[]; labelWidth?: number }) {
  const { theme } = useTheme();
  const dark = theme === "dark";
  const grid = dark ? "#22314E" : "#f1f5f9";
  const tick = dark ? "#7E90AE" : "#9ca3af";
  const axisLine = dark ? "#2A3A5A" : "#e5e7eb";
  const chartData = data.map((d) => ({ ...d, tickName: truncate(d.name, 34) }));

  return (
    <div className="w-full" style={{ height: Math.max(160, chartData.length * 40) }}>
      <ResponsiveContainer width="100%" height="100%">
        <RBarChart data={chartData} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }} barCategoryGap={8}>
          <CartesianGrid strokeDasharray="3 3" stroke={grid} horizontal={false} />
          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: tick }} tickLine={false} axisLine={{ stroke: axisLine }} />
          <YAxis type="category" dataKey="tickName" width={labelWidth} tick={{ fontSize: 11, fill: tick }} tickLine={false} axisLine={{ stroke: axisLine }} />
          <Tooltip content={<BarTooltip />} cursor={{ fill: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }} />
          <RBar dataKey="value" radius={[0, 4, 4, 0]} isAnimationActive={false}>
            {chartData.map((d) => (
              <Cell key={d.name} fill={d.color} />
            ))}
          </RBar>
        </RBarChart>
      </ResponsiveContainer>
    </div>
  );
}
